<template>
  <div class="solutions-view">
    <!-- Hero Section -->
    <section class="hero">
      <div class="hero-blob hero-blob-blue"></div>
      <div class="hero-blob hero-blob-orange"></div>
      <div class="hero-blob hero-blob-small"></div>
      <div class="hero-grid-pattern"></div>

      <div class="hero-content">
        <span class="hero-eyebrow">SOLUTIONS</span>
        <h1 class="hero-title">解决方案</h1>
        <p class="hero-subtitle">覆盖印前设计全流程的四大能力矩阵，助力企业从设计创作到生产交付高效协同</p>
        <div class="hero-stats">
          <div class="hero-stat">
            <div class="hero-stat-num">4</div>
            <div class="hero-stat-label">方案矩阵</div>
          </div>
          <div class="hero-stat-divider"></div>
          <div class="hero-stat">
            <div class="hero-stat-num">20+</div>
            <div class="hero-stat-label">功能模块</div>
          </div>
          <div class="hero-stat-divider"></div>
          <div class="hero-stat">
            <div class="hero-stat-num">30天</div>
            <div class="hero-stat-label">免费体验</div>
          </div>
        </div>
      </div>
    </section>

    <!-- Solutions List -->
    <section class="solutions-section">
      <div class="solutions-container">
        <div
            v-for="(sol, idx) in solutions"
            :key="sol.id"
            class="solution-block"
            :class="['solution-block--' + sol.color, { 'solution-block--reverse': idx % 2 === 1 }]"
            :style="{ '--block-delay': `${idx * 0.12}s` }"
        >
          <div class="solution-visual">
            <div class="solution-id">0{{ idx + 1 }}</div>
            <div class="solution-icon-wrap">
              <div class="solution-icon-glow"></div>
              <component :is="sol.icon" class="solution-icon" />
            </div>
            <div class="solution-visual-tag">{{ sol.eyebrow }}</div>
          </div>

          <div class="solution-body">
            <span class="solution-eyebrow">{{ sol.eyebrow }}</span>
            <h2 class="solution-title">{{ sol.title }}</h2>
            <p class="solution-desc">{{ sol.description }}</p>

            <ul class="solution-features">
              <li
                  v-for="(feat, i) in sol.features"
                  :key="i"
                  class="solution-feature"
                  :style="{ '--feat-delay': `${i * 0.08}s` }"
              >
                <span class="feature-check">
                  <el-icon><Check /></el-icon>
                </span>
                <span class="feature-text">{{ feat }}</span>
              </li>
            </ul>

            <div class="solution-scenarios">
              <h4 class="scenarios-title">
                <span class="scenarios-dot"></span>
                适用场景
              </h4>
              <div class="scenarios-tags">
                <span
                    v-for="(tag, i) in sol.scenarios"
                    :key="i"
                    class="scenario-tag"
                >{{ tag }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Bottom CTA -->
    <section class="cta-section">
      <div class="cta-bg"></div>
      <div class="cta-blob cta-blob-blue"></div>
      <div class="cta-blob cta-blob-orange"></div>
      <div class="cta-content">
        <span class="cta-eyebrow">EXPLORE MORE</span>
        <h2 class="cta-title">想了解更多产品详情？</h2>
        <p class="cta-subtitle">浏览我们的完整插件矩阵，找到最适合您业务场景的工具组合</p>
        <button class="cta-button" @click="router.push('/products?id=6')">
          <span>了解更多产品</span>
          <el-icon class="cta-icon"><ArrowRight /></el-icon>
        </button>
      </div>
    </section>
  </div>
</template>

<script setup>
import { useRouter } from 'vue-router';
import { Edit, Check, Setting, Opportunity, ArrowRight } from '@element-plus/icons-vue';

const router = useRouter();

const solutions = [
  {
    id: 1,
    eyebrow: 'DESIGN & CREATIVE',
    title: '设计与创作',
    icon: Edit,
    color: 'blue',
    description: '面向包装设计师与创意团队的专业工具集，从概念建模到精美排版，全流程提升设计效率与创作质量，让灵感顺畅落地。',
    features: [
      '智能 3D 包装建模与多角度实时预览',
      '矢量排版与版式智能优化建议',
      '可复用设计模板与素材库管理',
      '团队协作、评论与版本控制',
    ],
    scenarios: ['包装设计', '3D 建模', '版式排版', '品牌创意'],
  },
  {
    id: 2,
    eyebrow: 'QUALITY VERIFICATION',
    title: '质量验证',
    icon: Check,
    color: 'orange',
    description: '自动化校验印前文件合规性与能效标签，减少人工核对成本，确保输出文件满足客户要求与监管规范。',
    features: [
      '印前文件自动预检与纠错建议',
      '能效标签合规性智能校验',
      '颜色、字体与图像一致性检查',
      '生成可分享的详细质检报告',
    ],
    scenarios: ['印前预检', '能效标签', '合规校验', '质检报告'],
  },
  {
    id: 3,
    eyebrow: 'PRODUCTION READY',
    title: '生产就绪',
    icon: Setting,
    color: 'rose',
    description: '智能生成刀模、工艺文件与印刷就绪方案，无缝衔接设计与生产环节，显著缩短交付周期并降低出错率。',
    features: [
      '智能刀模自动生成与结构优化',
      '工艺文件一键导出与归档',
      '印刷参数智能匹配与推荐',
      '生产流程标准化模板配置',
    ],
    scenarios: ['刀模生成', '工艺文件', '印刷就绪', '生产交付'],
  },
  {
    id: 4,
    eyebrow: 'WORKFLOW AUTOMATION',
    title: '流程自动化',
    icon: Opportunity,
    color: 'purple',
    description: '基于可视化工作流引擎的批量处理与系统集成方案，打通设计、校验、生产各环节，实现端到端自动化。',
    features: [
      '可视化工作流编排与触发器配置',
      '批量文件处理与任务智能调度',
      'API 集成与第三方系统对接',
      '运行监控、日志与异常告警',
    ],
    scenarios: ['工作流编排', '批量处理', '系统集成', '运维监控'],
  },
];
</script>

<style scoped>
/* ============ Root ============ */
.solutions-view {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Microsoft YaHei', sans-serif;
  color: #0f172a;
  background:
      radial-gradient(ellipse 800px 600px at 10% 0%, rgba(47, 91, 255, 0.08), transparent 60%),
      radial-gradient(ellipse 700px 500px at 90% 10%, rgba(245, 158, 11, 0.06), transparent 55%),
      #f7f9fc;
  overflow-x: hidden;
  width: 100%;
  box-sizing: border-box;
}

/* ============ Hero ============ */
.hero {
  position: relative;
  padding: 120px 20px 110px;
  overflow: hidden;
  background:
      radial-gradient(ellipse 700px 500px at 15% 25%, rgba(47, 91, 255, 0.20), transparent 60%),
      radial-gradient(ellipse 600px 400px at 85% 35%, rgba(245, 158, 11, 0.16), transparent 60%),
      linear-gradient(180deg, #ffffff 0%, #f7f9fc 100%);
}

.hero-blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(60px);
  opacity: 0.6;
  pointer-events: none;
  animation: blobFloat 14s ease-in-out infinite;
}
.hero-blob-blue {
  width: 420px;
  height: 420px;
  background: radial-gradient(circle, rgba(47, 91, 255, 0.45), transparent 70%);
  top: -120px;
  left: -80px;
}
.hero-blob-orange {
  width: 360px;
  height: 360px;
  background: radial-gradient(circle, rgba(245, 158, 11, 0.4), transparent 70%);
  top: 40px;
  right: -60px;
  animation-delay: -5s;
}
.hero-blob-small {
  width: 220px;
  height: 220px;
  background: radial-gradient(circle, rgba(91, 124, 255, 0.35), transparent 70%);
  bottom: -60px;
  left: 38%;
  animation-delay: -9s;
}
@keyframes blobFloat {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(24px, -32px) scale(1.06); }
}

.hero-grid-pattern {
  position: absolute;
  inset: 0;
  background-image:
      linear-gradient(rgba(47, 91, 255, 0.04) 1px, transparent 1px),
      linear-gradient(90deg, rgba(47, 91, 255, 0.04) 1px, transparent 1px);
  background-size: 48px 48px;
  mask-image: radial-gradient(ellipse 80% 60% at 50% 40%, #000 30%, transparent 75%);
  -webkit-mask-image: radial-gradient(ellipse 80% 60% at 50% 40%, #000 30%, transparent 75%);
  pointer-events: none;
}

.hero-content {
  position: relative;
  z-index: 2;
  max-width: 900px;
  margin: 0 auto;
  text-align: center;
  animation: fadeInUp 0.9s cubic-bezier(0.16, 1, 0.3, 1) both;
}
@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

.hero-eyebrow {
  display: inline-block;
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 0.22em;
  color: #2f5bff;
  background: rgba(47, 91, 255, 0.08);
  padding: 7px 18px;
  border-radius: 100px;
  margin-bottom: 24px;
  border: 1px solid rgba(47, 91, 255, 0.18);
}
.hero-title {
  font-size: clamp(2.6rem, 6vw, 4.2rem);
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -0.02em;
  margin: 0 0 24px;
  background: linear-gradient(135deg, #1e293b 0%, #2f5bff 55%, #f59e0b 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
.hero-subtitle {
  font-size: 1.12rem;
  color: #475569;
  max-width: 640px;
  margin: 0 auto 56px;
  line-height: 1.7;
}
.hero-stats {
  display: flex;
  gap: 36px;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
}
.hero-stat-num {
  font-size: 2.3rem;
  font-weight: 800;
  line-height: 1;
  background: linear-gradient(135deg, #2f5bff 0%, #f59e0b 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
.hero-stat-label {
  font-size: 0.88rem;
  color: #475569;
  margin-top: 6px;
  letter-spacing: 0.04em;
}
.hero-stat-divider {
  width: 1px;
  height: 36px;
  background: linear-gradient(180deg, transparent, rgba(47, 91, 255, 0.25), transparent);
}

/* ============ Solutions Section ============ */
.solutions-section {
  padding: 80px 0 40px;
}
.solutions-container {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 80px;
}

.solution-block {
  display: grid;
  grid-template-columns: 0.85fr 1.15fr;
  gap: 56px;
  align-items: center;
  opacity: 0;
  transform: translateY(28px);
  animation: fadeInUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  animation-delay: var(--block-delay, 0s);
}
.solution-block--reverse {
  direction: rtl;
}
.solution-block--reverse > * {
  direction: ltr;
}

/* Visual side */
.solution-visual {
  position: relative;
  background: white;
  border-radius: 24px;
  padding: 64px 40px;
  border: 1px solid rgba(226, 232, 240, 0.8);
  box-shadow: 0 8px 32px rgba(15, 23, 42, 0.08);
  overflow: hidden;
  text-align: center;
  transition: transform 0.5s cubic-bezier(0.16, 1, 0.3, 1),
  box-shadow 0.5s cubic-bezier(0.16, 1, 0.3, 1);
}
.solution-visual::before {
  content: '';
  position: absolute;
  inset: 0;
  background:
      radial-gradient(ellipse 300px 220px at 30% 20%, rgba(47, 91, 255, 0.10), transparent 60%),
      radial-gradient(ellipse 260px 200px at 75% 80%, rgba(245, 158, 11, 0.08), transparent 60%);
  pointer-events: none;
}
.solution-block:hover .solution-visual {
  transform: translateY(-4px);
  box-shadow: 0 16px 48px rgba(47, 91, 255, 0.14);
}
.solution-id {
  position: absolute;
  top: 22px;
  right: 28px;
  font-size: 3.4rem;
  font-weight: 800;
  line-height: 1;
  color: rgba(47, 91, 255, 0.10);
  letter-spacing: -0.02em;
}
.solution-icon-wrap {
  position: relative;
  width: 120px;
  height: 120px;
  margin: 0 auto 28px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.solution-icon-glow {
  position: absolute;
  inset: -10px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(47, 91, 255, 0.28), transparent 70%);
  filter: blur(18px);
  animation: glow 4s ease-in-out infinite;
}
.solution-block--orange .solution-icon-glow {
  background: radial-gradient(circle, rgba(245, 158, 11, 0.28), transparent 70%);
}
.solution-block--rose .solution-icon-glow {
  background: radial-gradient(circle, rgba(244, 63, 94, 0.28), transparent 70%);
}
.solution-block--purple .solution-icon-glow {
  background: radial-gradient(circle, rgba(139, 92, 246, 0.28), transparent 70%);
}
@keyframes glow {
  0%, 100% { opacity: 0.55; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.08); }
}
.solution-icon {
  position: relative;
  width: 64px;
  height: 64px;
  color: white;
  background: linear-gradient(135deg, #2f5bff 0%, #5b7cff 100%);
  border-radius: 18px;
  padding: 16px;
  box-sizing: border-box;
  box-shadow: 0 12px 28px rgba(47, 91, 255, 0.32);
  transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}
.solution-block--orange .solution-icon {
  background: linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%);
  box-shadow: 0 12px 28px rgba(245, 158, 11, 0.32);
}
.solution-block--rose .solution-icon {
  background: linear-gradient(135deg, #f43f5e 0%, #fb7185 100%);
  box-shadow: 0 12px 28px rgba(244, 63, 94, 0.32);
}
.solution-block--purple .solution-icon {
  background: linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%);
  box-shadow: 0 12px 28px rgba(139, 92, 246, 0.32);
}
.solution-block:hover .solution-icon {
  transform: scale(1.1) rotate(3deg);
}
.solution-visual-tag {
  display: inline-block;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  color: #f59e0b;
  background: rgba(245, 158, 11, 0.08);
  padding: 6px 14px;
  border-radius: 100px;
  border: 1px solid rgba(245, 158, 11, 0.18);
}

/* Body side */
.solution-body {
  position: relative;
}
.solution-eyebrow {
  display: inline-block;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  color: #f59e0b;
  margin-bottom: 12px;
}
.solution-title {
  font-size: clamp(1.8rem, 3.2vw, 2.4rem);
  font-weight: 800;
  margin: 0 0 16px;
  line-height: 1.2;
  background: linear-gradient(135deg, #1e293b 0%, #2f5bff 70%, #f59e0b 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
.solution-desc {
  font-size: 1.02rem;
  color: #475569;
  line-height: 1.75;
  margin: 0 0 28px;
}
.solution-features {
  list-style: none;
  margin: 0 0 32px;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px 18px;
}
.solution-feature {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 0.95rem;
  color: #334155;
  line-height: 1.5;
  opacity: 0;
  transform: translateX(-12px);
  animation: fadeInLeft 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  animation-delay: calc(var(--block-delay, 0s) + 0.2s + var(--feat-delay, 0s));
}
@keyframes fadeInLeft {
  to { opacity: 1; transform: translateX(0); }
}
.feature-check {
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 7px;
  background: linear-gradient(135deg, #2f5bff 0%, #5b7cff 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.75rem;
  box-shadow: 0 4px 10px rgba(47, 91, 255, 0.28);
  margin-top: 2px;
}
.feature-text {
  flex: 1;
}

.solution-scenarios {
  background: rgba(47, 91, 255, 0.04);
  border: 1px solid rgba(47, 91, 255, 0.12);
  border-radius: 16px;
  padding: 20px 22px;
}
.scenarios-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.82rem;
  font-weight: 700;
  color: #475569;
  margin: 0 0 14px;
  letter-spacing: 0.04em;
}
.scenarios-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: linear-gradient(135deg, #2f5bff, #f59e0b);
  box-shadow: 0 0 0 4px rgba(47, 91, 255, 0.12);
}
.scenarios-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.scenario-tag {
  font-size: 0.85rem;
  font-weight: 600;
  color: #1f43cc;
  background: white;
  padding: 6px 14px;
  border-radius: 100px;
  border: 1px solid rgba(47, 91, 255, 0.2);
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  cursor: default;
}
.scenario-tag:hover {
  background: linear-gradient(135deg, #2f5bff 0%, #5b7cff 100%);
  color: white;
  border-color: transparent;
  transform: translateY(-2px);
  box-shadow: 0 6px 14px rgba(47, 91, 255, 0.28);
}

/* ============ Bottom CTA ============ */
.cta-section {
  position: relative;
  margin: 60px 20px 100px;
  border-radius: 28px;
  overflow: hidden;
  padding: 80px 32px;
  text-align: center;
}
.cta-bg {
  position: absolute;
  inset: 0;
  background:
      radial-gradient(ellipse 600px 300px at 20% 30%, rgba(47, 91, 255, 0.12), transparent 60%),
      radial-gradient(ellipse 500px 280px at 80% 70%, rgba(245, 158, 11, 0.10), transparent 60%),
      linear-gradient(135deg, #f0f6ff 0%, #fff9f0 100%);   /* 浅色渐变 */
  z-index: 0;
}
.cta-blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(50px);
  pointer-events: none;
  z-index: 1;
}
.cta-blob-blue {
  width: 240px;
  height: 240px;
  background: radial-gradient(circle, rgba(47, 91, 255, 0.55), transparent 70%);
  top: -60px;
  left: 10%;
  animation: blobFloat 12s ease-in-out infinite;
}
.cta-blob-orange {
  width: 220px;
  height: 220px;
  background: radial-gradient(circle, rgba(245, 158, 11, 0.5), transparent 70%);
  bottom: -60px;
  right: 12%;
  animation: blobFloat 12s ease-in-out infinite;
  animation-delay: -6s;
}
.cta-content {
  position: relative;
  z-index: 2;
  max-width: 640px;
  margin: 0 auto;
}
.cta-eyebrow {
  display: inline-block;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  color: #2f5bff;
  margin-bottom: 14px;
}
.cta-title {
  font-size: clamp(1.8rem, 3.5vw, 2.4rem);
  font-weight: 800;
  margin: 0 0 14px;
  color: #0f172a;
  line-height: 1.2;
}
.cta-subtitle {
  font-size: 1.02rem;
  color: #475569;
  line-height: 1.7;
  margin: 0 0 32px;
}
.cta-button {
  background: linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%);
  color: white;
  font-weight: 600;
  border: none;
  padding: 16px 38px;
  font-size: 1.05rem;
  border-radius: 12px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 12px 28px rgba(245, 158, 11, 0.4);
  transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1),
  box-shadow 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}
.cta-button:hover {
  transform: translateY(-4px);
  box-shadow: 0 18px 38px rgba(245, 158, 11, 0.5);
}
.cta-button .cta-icon {
  transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  font-size: 1rem;
}
.cta-button:hover .cta-icon {
  transform: translateX(4px);
}

/* ============ Responsive ============ */
@media (max-width: 992px) {
  .solution-block {
    grid-template-columns: 1fr;
    gap: 32px;
  }
  .solution-block--reverse {
    direction: ltr;
  }
  .solution-visual {
    padding: 48px 32px;
  }
  .solutions-container {
    gap: 64px;
  }
}

@media (max-width: 768px) {
  .hero {
    padding: 70px 20px 70px;
  }
  .hero-subtitle {
    font-size: 1rem;
    margin-bottom: 40px;
  }
  .hero-stats {
    gap: 18px;
  }
  .hero-stat-num {
    font-size: 1.7rem;
  }
  .hero-stat-label {
    font-size: 0.8rem;
  }
  .hero-stat-divider {
    display: none;
  }
  .solutions-section {
    padding: 50px 16px 30px;
  }
  .solutions-container {
    gap: 48px;
  }
  .solution-visual {
    padding: 40px 24px;
  }
  .solution-icon-wrap {
    width: 100px;
    height: 100px;
  }
  .solution-icon {
    width: 54px;
    height: 54px;
    padding: 14px;
    border-radius: 22px;
  }
  .solution-id {
    font-size: 2.6rem;
    top: 16px;
    right: 20px;
  }
  .solution-features {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  .cta-section {
    margin: 40px 16px 70px;
    padding: 56px 20px;
    border-radius: 22px;
  }
}

@media (max-width: 576px) {
  .hero {
    padding: 56px 16px 56px;
  }
  .hero-eyebrow {
    font-size: 0.72rem;
    padding: 6px 14px;
  }
  .cta-button {
    padding: 14px 26px;
    font-size: 0.98rem;
  }
  .scenario-tag {
    font-size: 0.8rem;
    padding: 5px 12px;
  }
}
</style>
